import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { GALLERY_ITEMS, GALLERY_TAGS } from "../config";
import { Reveal, SectionLabel } from "../components/Reveal";

export default function Stories() {
  const [filter, setFilter] = useState("All");
  const items = filter === "All" ? GALLERY_ITEMS : GALLERY_ITEMS.filter((g) => g.tag === filter);
  const slug = (t) => t.toLowerCase().replace(/[^a-z]+/g, "-");

  return (
    <main>
      <section className="relative overflow-hidden bg-[#14717A] pb-16 pt-32 sm:pb-20 sm:pt-40">
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-[#4CA6AF]/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-[#0d5259]/60 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-6 sm:px-8">
          <Reveal>
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#9fd8dd]">Treatment Gallery</p>
            <h1 data-testid="stories-title" className="mt-4 font-heading text-4xl font-semibold text-white sm:text-6xl">
              Patient Stories
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#f7fafb] py-12 sm:py-16" data-testid="stories-grid-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-8">
          <Reveal>
            <div className="flex flex-wrap gap-2.5" data-testid="gallery-filters">
              {["All", ...GALLERY_TAGS].map((t) => (
                <button
                  key={t}
                  data-testid={`gallery-filter-${slug(t)}`}
                  onClick={() => setFilter(t)}
                  className={`rounded-full px-5 py-2.5 text-xs font-bold tracking-wide transition-all duration-300 ${
                    filter === t
                      ? "bg-[#14717A] text-white shadow-[0_10px_24px_rgba(20,113,122,0.3)]"
                      : "border border-[#DCE4E8] bg-white text-[#5f7482] hover:border-[#4CA6AF] hover:text-[#14717A]"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </Reveal>
          <motion.div layout className="mx-auto mt-8 grid max-w-5xl grid-cols-2 gap-6 sm:grid-cols-3 sm:gap-7 lg:grid-cols-4" data-testid="stories-grid">
            <AnimatePresence mode="popLayout">
              {items.map((g) => (
                <motion.div
                  layout
                  key={g.src}
                  initial={{ opacity: 0, scale: 0.92 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.92 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="group relative overflow-hidden rounded-2xl shadow-sm"
                  data-testid={`story-image-${g.caseNo}`}
                >
                  <img
                    src={g.src}
                    alt="Patient transformation"
                    loading="lazy"
                    className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          <Reveal className="mt-14">
            <div className="flex justify-center">
              <Link
                to="/book"
                data-testid="stories-book-btn"
                className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-[#14717A] px-8 py-4 text-sm font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#0d5259]"
              >
                Book Appointment
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
